import './DashboardPage.css';
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Outlet, useParams } from 'react-router-dom';
import { useLoader } from 'src/provider/LoaderProvider';
import { clsx } from 'clsx';
import { Menu } from 'src/components/Menu/Menu';
import { CompanySelectComponent } from 'src/components/CompanySelectComponent/CompanySelectComponent';
import { useTheme } from 'src/provider/ThemeProvider';
import { useGetUsersMeQuery } from 'src/services/usersService';
import { useGetAllProjectsQuery, useGetProjectByIdQuery } from 'src/services';
import { PATH_DASHBOARD, PATH_HOME, PATH_ONBOARDING } from 'src/constants/spa-routes';
import { ChangePasswordModal } from 'src/components/ChangePasswordModal/ChangePasswordModal';
import { ROLES } from 'src/constants/roles';
import {
  isProductKey,
  ProductsType,
  ProductsTypeColor,
  toProductTypeBack,
} from 'src/constants/products';
import { useAppSelector, useAppDispatch } from 'src/store';
import { AssignProjectToKnowledgeBase } from 'src/components/AssignProjectToKnowledgeBase/AssignProjectToKnowledgeBase';
import { useGetAccountsQuery } from 'src/services/accountsService';
import { SubscriptionsStatus } from 'src/constants/subscriptions';
import { updatePlanState } from 'src/store/slices/planSlice';
import { SuccessOnboardingPopup } from 'src/pages/DashboardPage/SuccessOnboardingPopup/SuccessOnboardingPopup';
import { usePHIndentity } from 'src/hooks/usePHIndentity';
import { HeaderBanner } from 'src/components/HeaderBanner/HeaderBanner';
import { VersionToggleGroup } from 'src/components/VersionToggleGroup/VersionToggleGroup';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loader = useLoader();
  const { id } = useParams();
  const { config, setTheme } = useTheme();
  const searchParams = new URLSearchParams(location.search);
  const searchProjectType = searchParams.get('type');
  const dispatch = useAppDispatch();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || searchProjectType;
  const isDashboardProjects = location.pathname.includes(PATH_DASHBOARD.PROJECTS);

  const { data: userInfo } = useGetUsersMeQuery();
  const { data: projects, isLoading: isLoadingProjects } = useGetAllProjectsQuery();
  const { data: accounts, isLoading: isLoadingAccounts } = useGetAccountsQuery();

  const { isError, error } = useGetProjectByIdQuery(
    { id: id },
    { skip: !id || !isDashboardProjects },
  );

  usePHIndentity();

  useEffect(() => {
    if (searchProjectType) {
      setTheme(isProductKey(projectTypeVar) ? ProductsTypeColor[projectTypeVar] : undefined);
    }
  }, [searchProjectType, location]);

  useEffect(() => {
    if (isDashboardProjects && !toProductTypeBack(projectTypeVar)) {
      navigate(PATH_HOME.ROOT);
    }
  }, [projectTypeVar]);

  useEffect(() => {
    if (accounts) {
      const isInactivePlan = accounts?.general_plan?.status === SubscriptionsStatus.inactive;
      const isTrialPlan = accounts?.general_plan?.plan?.plan_type === 'trial';

      dispatch(updatePlanState({ isInactivePlan, isTrialPlan }));
    }
  }, [accounts]);

  useEffect(() => {
    if (isError && isDashboardProjects && 'status' in error && error.status === 404) {
      loader.hide();
      navigate(PATH_HOME.ROOT);
    }
  }, [isError]);

  const isDashboard = location.pathname === PATH_DASHBOARD.ROOT;

  useLoaderWhile(isLoadingProjects, isLoadingAccounts);

  useEffect(() => {
    if (projects) {
      const skipOnboarding = sessionStorage.getItem('skip_onboarding');
      const cosupportManager = userInfo?.role === ROLES.COSSUPORT_MANAGER;

      // navigate to onboarding first step to create a project
      if (!cosupportManager && !skipOnboarding && !projects.length) {
        navigate(PATH_ONBOARDING.ROOT);
        return;
      }

      projects.length &&
        isDashboard &&
        navigate(
          `${PATH_DASHBOARD.PROJECTS}/${projects[0].id}${projects[0].project_type === ProductsType.customer ? '/control-desk' : ''}?type=${projects[0].project_type}`,
          {
            replace: true,
          },
        );
    }
  }, [projects]);

  const isProjectLocation = location.pathname.includes('projects');
  const isAccountLocation = location.pathname.includes('account');
  const isKnowledgeBaseLocation = location.pathname.includes('ai-knowledge-base');
  const isWorkspaceAllowedByRole = [ROLES.COSSUPORT_MANAGER, ROLES.ADMIN, ROLES.MANAGER].includes(
    userInfo?.role,
  );

  return (
    <React.Fragment>
      <HeaderBanner />
      <div className={`dashboard-page-container ${config?.className || ''}`}>
        <SuccessOnboardingPopup />
        <Menu />
        <div className="dashboard-container max-h-screen overflow-auto">
          <div className="dashboard-header w-full min-h-[60px] mx-auto relative border-b border-[#E3E6E8]">
            <div className="absolute right-5 top-3">
              <div
                className={clsx(
                  'flex flex-col gap-[14px] relative z-40',
                  isProjectLocation && 'gap-8',
                )}
              >
                <div
                  className={clsx(
                    'breadcrumbs-container flex gap-2.5 h-9 items-center justify-between',
                  )}
                >
                  <div className="flex gap-2.5">
                    {isAccountLocation && (
                      <div className="breadcrumbs">
                        <ChangePasswordModal />
                      </div>
                    )}
                    {isKnowledgeBaseLocation && id && <AssignProjectToKnowledgeBase />}
                    {userInfo?.role === ROLES.COSSUPORT_MANAGER && <VersionToggleGroup />}
                    {!(isKnowledgeBaseLocation && id) &&
                      isWorkspaceAllowedByRole &&
                      Boolean(userInfo?.managed_accounts?.length) && (
                        <div className="breadcrumbs">
                          <CompanySelectComponent />
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Outlet />
        </div>
      </div>
    </React.Fragment>
  );
};

export default DashboardPage;
